import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('login'); // 'login' ou '2fa'
  const [tempUserId, setTempUserId] = useState(null);
  const [code, setCode] = useState('');

  const { login, loginWithGoogle, verify2FA } = useAuth();
  const navigate = useNavigate();

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    try {
      await loginWithGoogle(credentialResponse.credential);
      toast.success('Login com Google realizado com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao autenticar com o Google');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await login(email, password);
      // Se a API retornar que o usuário tem 2FA...
      if (res?.requires_2fa) {
        setTempUserId(res.temp_user_id);
        setStep('2fa');
        toast.info('Código de segurança enviado para seu e-mail.');
      } else {
        toast.success('Login realizado com sucesso!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verify2FA(tempUserId, code);
      toast.success('Login realizado com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código de verificação incorreto ou expirado');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-8">
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_ee8574e7-2cba-4c3d-8cd6-b25471143ee6/artifacts/31800g12_Logo%20Login%20Sites.png" 
              alt="Kode Studio" 
              className="h-16 mx-auto mb-4"
            />
            <h1 className="text-2xl font-heading font-bold text-slate-900">Bem-vindo de volta</h1>
            <p className="text-slate-500 mt-2">Entre com suas credenciais</p>
          </div>

          {step === 'login' ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  data-testid="login-email-input"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                  className="mt-1"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Verificando...' : 'Entrar'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerify2FA} className="space-y-4">
               <div>
                 <Label htmlFor="code" className="text-center block mb-2 font-medium">
                   O código foi enviado para <b>{email}</b>
                 </Label>
                 <Input
                   id="code"
                   type="text"
                   value={code}
                   onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} // Apenas números
                   placeholder="000000"
                   maxLength={6}
                   required
                   className="mt-1 text-center text-3xl tracking-[0.5em] font-mono py-6 font-bold text-slate-800 bg-slate-50 border-slate-200 focus:border-primary focus:ring-primary/20"
                 />
                 <p className="text-xs text-slate-500 mt-3 text-center">
                   Digite o código de 6 dígitos.<br/>
                   Ele expira em 10 minutos.
                 </p>
               </div>
               <div className="pt-2">
                 <Button type="submit" className="w-full bg-primary hover:bg-primary/90 font-medium" disabled={loading || code.length < 6}>
                   {loading ? 'Acessando...' : 'Confirmar Código'}
                 </Button>
                 <Button type="button" variant="ghost" className="w-full mt-2 text-slate-500 hover:text-slate-800" onClick={() => setStep('login')}>
                   Voltar ao login normal
                 </Button>
               </div>
            </form>
          )}

          {step === 'login' && (
            <>
              <div className="relative my-6 text-center">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
                <div className="relative flex justify-center text-sm"><span className="bg-white px-2 text-slate-500">Ou continue com</span></div>
          </div>

          <div className="w-full flex items-center justify-center mb-6">
            <div className="flex justify-center w-fit mx-auto">
              <GoogleLogin 
                onSuccess={handleGoogleSuccess}
                onError={() => toast.error('Falha ao autenticar com o Google.')}
                shape="rectangular"
                theme="outline"
                size="large"
                locale="pt-BR"
              />
            </div>
          </div>

          <div className="mt-6 text-center text-sm text-slate-500">
              <p>Ainda não possui uma conta? <Link to="/register" className="text-primary hover:underline font-semibold">Registre-se agora</Link></p>
          </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}